import api from '../api/apiClient';
import { withSurface } from '../security/surfaceScope';

/**
 * Graph Lens Service — the read contract over the Neo4j entity graph.
 *
 * Pages ask for a named lens and never write Cypher. That matters because the ontology
 * will keep moving as integrations are added: when it last moved, six hand-written
 * queries in agent-atlas silently returned zero rows and rendered empty pages with no
 * error anywhere. A lens name is a stable contract; a Cypher string is not.
 *
 * Every response is an envelope, never a bare payload:
 *
 *   { lens, description, coverage: { status, caveats?, unlocked_by?, message? }, data }
 *
 * `coverage.status` is `available` | `degraded` | `unavailable`, and `data` is **null**
 * (not an empty array) when unavailable. Rendering must branch on it — see
 * `isUnavailable` below. An empty list drawn as "no results" is exactly the misreading
 * the coverage model exists to prevent: a tenant with no data-plane integration has not
 * been told there is nothing to find, they have been told nothing.
 */
const GRAPH = '/graph/api/v1';

export const LENS = {
  ENTITY_MAP: 'entity_map',
  // Ported from agent-atlas /graph/*. Same questions, but each answer now carries what
  // this tenant cannot see, and the NAME survives the next ontology change -- which is
  // what those endpoints lacked when six of them began returning zero rows silently.
  GRAPH_STATS: 'graph_stats',
  SURFACES: 'surfaces',
  AGENTS: 'agents',
  MODELS_IN_USE: 'models_in_use',
  SHADOW_AI: 'shadow_ai',
  FRAMEWORK_EXPOSURE: 'framework_exposure',
  DETECTION_MAP: 'detection_map',
  POLICY_VIOLATIONS: 'policy_violations',
  ALERT_SUMMARY: 'alert_summary',
  RISKY_AGENTS: 'risky_agents',
  NETWORK: 'network',
  AGENT_LINEAGE: 'agent_lineage',
  REACH_RANKED: 'reach_ranked',
  IDENTITY_JOURNEY: 'identity_journey',
  BLAST_RADIUS: 'blast_radius',
  EXPOSURE: 'exposure',
  ATTACK_PATH: 'attack_path',
  TOXIC_COMBINATIONS: 'toxic_combinations',
  ROOT_CAUSE: 'root_cause',
  CONTROL_GAPS: 'control_gaps',
  UNGOVERNED_DATA: 'ungoverned_data',
  COST_ATTRIBUTION: 'cost_attribution',
  CROSS_SURFACE_RISK: 'cross_surface_risk',
  ACCESS_REVIEW: 'access_review',
  INCIDENT_MAP: 'incident_map',
};

/** FastAPI reads a list as the repeated key (seeds=a&seeds=b), not axios' `seeds[]=a`. */
const REPEAT = { indexes: null };

export const graphLensService = {
  /**
   * Run a lens. `params` are lens-specific (seed, email, node_id, depth, …). `config` is
   * extra axios config (a list param needs `paramsSerializer`).
   */
  async runLens(name, params = {}, config = {}) {
    // withSurface: mounted inside a product module, the pinned surface travels with
    // the request so the context graph shows that surface's estate. A person is one
    // merged node carrying every surface they appear on, so unscoped this drags a
    // Gemini page's graph through their Copilot apps and cloud footprint.
    const { data } = await api.get(`${GRAPH}/lens/${name}`, { ...config, params: withSurface(params) });
    return data;
  },

  /*
   * The lenses an entity page offers over its Context Graph. Each returns the whole
   * envelope (coverage + data); the canvas renders `coverage` itself.
   *
   *   blast_radius / exposure  data: {seed, depth, reached, by_type: [{type, count, examples}]}
   *                            (outgoing vs incoming reach; `examples` are names, not ids)
   *   incident_map             data: {seeds, nodes: [{id, ...}], edges}
   *   agent_lineage            data: {models, tools, datastores, subagents, detections,
   *                                   frameworks, policies, invoked_by} — lists of names
   *   identity_journey         data: {identity, activity: [{target, target_type, action, ...}]}
   *   access_review            data: {identity, reaches: {by_type: [...]}}
   */
  lensBlastRadius(seed, p = {})   { return this.runLens(LENS.BLAST_RADIUS, { ...p, seed }); },
  lensExposure(seed, p = {})      { return this.runLens(LENS.EXPOSURE, { ...p, seed }); },
  lensIncidentMap(seeds, p = {}) {
    const list = (Array.isArray(seeds) ? seeds : [seeds]).filter(Boolean);
    return this.runLens(LENS.INCIDENT_MAP, { ...p, seeds: list }, { paramsSerializer: REPEAT });
  },
  lensAgentLineage(seed, p = {})  { return this.runLens(LENS.AGENT_LINEAGE, { ...p, seed }); },
  lensIdentityJourney(email, p = {}) { return this.runLens(LENS.IDENTITY_JOURNEY, { ...p, email }); },
  lensAccessReview(seed, email, p = {}) { return this.runLens(LENS.ACCESS_REVIEW, { ...p, seed, email }); },

  /**
   * What this tenant can and cannot see, plus the verdict for every lens.
   *
   * No organization argument: apiClient's request interceptor injects `organization_id`
   * from the stored user on every call. Accepting one here would let a page override
   * tenant scope from the browser, and tenant isolation in this graph is a property on
   * every node with no database-level enforcement behind it.
   */
  async coverage() {
    const { data } = await api.get(`${GRAPH}/coverage`);
    return data;
  },

  /**
   * Compatibility shims for the agent-atlas `/graph/*` endpoints these replaced.
   *
   * They unwrap the lens envelope so existing callers keep their old shape. New code
   * should call `runLens` directly and render `coverage` -- the envelope is the point,
   * and unwrapping it throws away the tenant's "what am I not seeing" answer.
   */
  async graphStats()        { return (await this.runLens(LENS.GRAPH_STATS)).data?.stats?.[0] ?? {}; },
  async graphSurfaces()     { return (await this.runLens(LENS.SURFACES)).data?.surfaces ?? []; },
  async graphAgents(p = {}) { return (await this.runLens(LENS.AGENTS, p)).data?.agents ?? []; },
  async graphModels()       { return (await this.runLens(LENS.MODELS_IN_USE)).data?.models ?? []; },
  async graphShadowAi(p = {}) { return (await this.runLens(LENS.SHADOW_AI, p)).data?.shadow_ai ?? []; },
  async graphFrameworkExposure() { return (await this.runLens(LENS.FRAMEWORK_EXPOSURE)).data?.framework_exposure ?? []; },
  async graphDetectionMap() { return (await this.runLens(LENS.DETECTION_MAP)).data?.detection_map ?? []; },
  async graphPolicyViolations(p = {}) { return (await this.runLens(LENS.POLICY_VIOLATIONS, p)).data?.policy_violations ?? []; },
  async graphAlerts()       { return (await this.runLens(LENS.ALERT_SUMMARY)).data?.alerts ?? []; },
  async graphRiskyAgents(p = {}) { return (await this.runLens(LENS.RISKY_AGENTS, p)).data?.risky_agents ?? []; },
  async graphNeighborhood(seed, p = {}) { return this.runLens(LENS.ENTITY_MAP, { seed, ...p }); },
  async graphBlastRadius(seed, p = {}) { return this.runLens(LENS.BLAST_RADIUS, { seed, ...p }); },
  async graphNetwork(p = {})   { return (await this.runLens(LENS.NETWORK, p)).data ?? { nodes: [], edges: [] }; },
  async graphLineage(seed)     { return (await this.runLens(LENS.AGENT_LINEAGE, { seed })).data ?? {}; },
  async graphReachRanked(p = {}) { return (await this.runLens(LENS.REACH_RANKED, p)).data?.agents ?? []; },

  /** Every lens and the planes it needs. Static — safe to cache. */
  async catalog() {
    const { data } = await api.get(`${GRAPH}/lenses`);
    return data.lenses;
  },
};

/** True when a required plane has no source — render the explanation, not an empty state. */
export const isUnavailable = (envelope) => envelope?.coverage?.status === 'unavailable';

/** True when the lens answered but something is missing. `caveats` says what. */
export const isDegraded = (envelope) => envelope?.coverage?.status === 'degraded';

/**
 * Caveats that mean the result UNDERSTATES risk, separated from those that merely omit
 * detail. Only the first kind can mislead someone into inaction, so the UI shows it as a
 * warning rather than a footnote.
 */
export const understatesRisk = (envelope) =>
  (envelope?.coverage?.caveats || []).some((c) => c.includes('UNDERSTATE'));

export default graphLensService;
